import { useState } from 'react';
import axios from 'axios';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { MessageSquare, Loader2 } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const testimonialSchema = z.object({
  student_name: z.string().min(2, 'Name must be at least 2 characters'),
  student_photo: z.string().url('Must be a valid URL').optional().or(z.literal('')),
  feedback: z.string().min(20, 'Feedback must be at least 20 characters'),
  course_taken: z.string().min(2, 'Course is required'),
});

export default function StudentTestimonial() {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm({
    resolver: zodResolver(testimonialSchema),
  });

  const getAuthHeaders = () => {
    const token = localStorage.getItem('studentToken');
    return { headers: { Authorization: `Bearer ${token}` } };
  };

  const onSubmit = async (data) => {
    setIsSubmitting(true);
    try {
      await axios.post(`${API}/testimonials`, data, getAuthHeaders());
      toast.success('Testimonial submitted! It will appear on the website after admin approval.');
      reset();
    } catch (error) {
      console.error('Error submitting testimonial:', error);
      toast.error('Failed to submit testimonial');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="bg-white rounded-xl p-6 shadow-sm">
      <div className="mb-6">
        <div className="flex items-center gap-3 mb-2">
          <MessageSquare className="h-8 w-8 text-brand-blue" />
          <h1 className="text-3xl font-bold text-brand-dark font-display">Add Your Testimonial</h1>
        </div>
        <p className="text-slate-600">Share your learning experience with Tutor Flex</p>
      </div>

      <div className="max-w-2xl">
        <div className="bg-blue-50 p-4 rounded-lg border border-blue-200 mb-6">
          <p className="text-sm text-blue-900">
            <strong>Note:</strong> Your testimonial will be reviewed by the admin before appearing on the website.
            This helps maintain quality and authenticity.
          </p>
        </div>

        <form onSubmit={handleSubmit(onSubmit)} data-testid="testimonial-form" className="space-y-6">
          <div>
            <Label htmlFor="student_name" className="text-brand-dark font-medium">Your Name *</Label>
            <Input
              id="student_name"
              data-testid="testimonial-name-input"
              {...register('student_name')}
              placeholder="Enter your full name"
              className="mt-2 rounded-lg"
            />
            {errors.student_name && (
              <p className="text-red-500 text-sm mt-1">{errors.student_name.message}</p>
            )}
          </div>

          <div>
            <Label htmlFor="student_photo" className="text-brand-dark font-medium">
              Your Photo URL (Optional)
            </Label>
            <Input
              id="student_photo"
              data-testid="testimonial-photo-input"
              {...register('student_photo')}
              placeholder="https://example.com/your-photo.jpg"
              className="mt-2 rounded-lg"
            />
            <p className="text-sm text-slate-500 mt-1">Leave empty if you don't want to display your photo</p>
            {errors.student_photo && (
              <p className="text-red-500 text-sm mt-1">{errors.student_photo.message}</p>
            )}
          </div>

          <div>
            <Label htmlFor="course_taken" className="text-brand-dark font-medium">Course Taken *</Label>
            <Input
              id="course_taken"
              data-testid="testimonial-course-input"
              {...register('course_taken')}
              placeholder="e.g., Engineering Mathematics, GATE Preparation - EC"
              className="mt-2 rounded-lg"
            />
            {errors.course_taken && (
              <p className="text-red-500 text-sm mt-1">{errors.course_taken.message}</p>
            )}
          </div>

          <div>
            <Label htmlFor="feedback" className="text-brand-dark font-medium">Your Feedback *</Label>
            <Textarea
              id="feedback"
              data-testid="testimonial-feedback-input"
              {...register('feedback')}
              placeholder="Share your experience, what you learned, how it helped you..."
              rows={6}
              className="mt-2 rounded-lg"
            />
            <p className="text-sm text-slate-500 mt-1">Minimum 20 characters</p>
            {errors.feedback && (
              <p className="text-red-500 text-sm mt-1">{errors.feedback.message}</p>
            )}
          </div>

          <Button
            type="submit"
            data-testid="testimonial-submit-btn"
            disabled={isSubmitting}
            className="w-full bg-brand-blue hover:bg-blue-600"
          >
            {isSubmitting ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Submitting...
              </>
            ) : (
              'Submit Testimonial'
            )}
          </Button>
        </form>
      </div>
    </div>
  );
}
